import Decimal from "decimal.js";
import { FISCAL_INDICATOR_CUTOFFS, inpcForMonth, monthKey, previousMonthKey, udiForDate } from "./fiscal-indicators";
import { decimal, formatMxn, minDecimal, money, nonNegative } from "./money";
import type { TaxBracket } from "./parameters";
import { LEGAL_SOURCES, parametersFor } from "./parameters";
import { calculateTaxTableBreakdown } from "./tax";
import type {
  CalculationResult,
  RealEstateDeductionLine,
  RealEstateSaleInput,
} from "./types";

const HOUSE_EXEMPTION_UDI = 700_000;

const ANNUAL_TAX_TABLE_2024_2025: TaxBracket[] = [
  { lower: 0.01, upper: 8_952.49, fixed: 0, rate: 0.0192 },
  { lower: 8_952.5, upper: 75_984.55, fixed: 171.88, rate: 0.064 },
  { lower: 75_984.56, upper: 133_536.07, fixed: 4_461.94, rate: 0.1088 },
  { lower: 133_536.08, upper: 155_229.8, fixed: 10_723.55, rate: 0.16 },
  { lower: 155_229.81, upper: 185_852.57, fixed: 14_194.54, rate: 0.1792 },
  { lower: 185_852.58, upper: 374_837.88, fixed: 19_682.13, rate: 0.2136 },
  { lower: 374_837.89, upper: 590_795.99, fixed: 60_049.4, rate: 0.2352 },
  { lower: 590_796, upper: 1_127_926.84, fixed: 110_842.74, rate: 0.3 },
  { lower: 1_127_926.85, upper: 1_503_902.46, fixed: 271_981.99, rate: 0.32 },
  { lower: 1_503_902.47, upper: 4_511_707.37, fixed: 392_294.17, rate: 0.34 },
  { lower: 4_511_707.38, upper: Number.POSITIVE_INFINITY, fixed: 1_414_947.85, rate: 0.35 },
];

const ANNUAL_TAX_TABLE_2026: TaxBracket[] = [
  { lower: 0.01, upper: 10_135.11, fixed: 0, rate: 0.0192 },
  { lower: 10_135.12, upper: 86_022.11, fixed: 194.59, rate: 0.064 },
  { lower: 86_022.12, upper: 151_176.19, fixed: 5_051.37, rate: 0.1088 },
  { lower: 151_176.2, upper: 175_735.66, fixed: 12_140.13, rate: 0.16 },
  { lower: 175_735.67, upper: 210_403.69, fixed: 16_069.64, rate: 0.1792 },
  { lower: 210_403.7, upper: 424_353.97, fixed: 22_282.14, rate: 0.2136 },
  { lower: 424_353.98, upper: 668_840.14, fixed: 67_981.92, rate: 0.2352 },
  { lower: 668_840.15, upper: 1_276_925.98, fixed: 125_485.07, rate: 0.3 },
  { lower: 1_276_925.99, upper: 1_702_567.97, fixed: 307_910.81, rate: 0.32 },
  { lower: 1_702_567.98, upper: 5_107_703.92, fixed: 444_116.23, rate: 0.34 },
  { lower: 5_107_703.93, upper: Number.POSITIVE_INFINITY, fixed: 1_601_862.46, rate: 0.35 },
];

export const REAL_ESTATE_SOURCES = {
  exemption: "https://wwwmat.sat.gob.mx/articulo/15199/articulo-93",
  deductions: "https://wwwmat.sat.gob.mx/articulo/31968/articulo-121",
  adjustment: "https://wwwmat.sat.gob.mx/articulo/86001/articulo-124",
  federalPayment: "https://wwwmat.sat.gob.mx/articulo/18353/articulo-126",
  statePayment: "https://wwwmat.sat.gob.mx/articulo/32168/articulo-127",
  tariff: "https://wwwmat.sat.gob.mx/articulo/35375/regla-3.15.4",
} as const;

function percentage(value: number | undefined, fallback: number): Decimal {
  const safe = value ?? fallback;
  if (!Number.isFinite(safe) || safe < 0 || safe > 100) {
    throw new Error("Los porcentajes deben estar entre 0 y 100.");
  }
  return decimal(safe).div(100);
}

function validateInput(input: RealEstateSaleInput): void {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.saleDate) || !/^\d{4}-\d{2}-\d{2}$/.test(input.acquisitionDate)) {
    throw new Error("Captura fechas válidas en formato AAAA-MM-DD.");
  }
  if (input.acquisitionDate >= input.saleDate) {
    throw new Error("La fecha de adquisición debe ser anterior a la de venta.");
  }
  if (!Number.isFinite(input.salePrice) || input.salePrice <= 0) {
    throw new Error("El precio de venta debe ser mayor que cero.");
  }
  if (!Number.isFinite(input.acquisitionCost) || input.acquisitionCost < 0) {
    throw new Error("El costo de adquisición no puede ser negativo.");
  }
}

function completedYears(from: string, to: string): number {
  const yearDifference = Number(to.slice(0, 4)) - Number(from.slice(0, 4));
  const anniversaryPending = to.slice(5) < from.slice(5);
  return Math.max(0, yearDifference - (anniversaryPending ? 1 : 0));
}

function elapsedTaxYears(from: string, to: string): number {
  return Math.max(1, Math.min(20, completedYears(from, to)));
}

function constructionReduction(original: Decimal, years: number): Decimal {
  const reduced = original.times(decimal(1).minus(decimal(0.03).times(years)));
  return Decimal.max(reduced, original.times(0.2), 0);
}

function deductionLine(
  key: string,
  label: string,
  original: Decimal,
  adjustedBase: Decimal,
  updateFactor: Decimal,
  nonExemptRatio: Decimal,
): RealEstateDeductionLine {
  const updated = adjustedBase.times(updateFactor);
  return {
    key,
    label,
    original: money(original),
    adjustedBase: money(adjustedBase),
    updateFactor: updateFactor.toDecimalPlaces(6).toNumber(),
    updated: money(updated),
    deductible: money(updated.times(nonExemptRatio)),
  };
}

export function calculateRealEstateIsr(input: RealEstateSaleInput): CalculationResult {
  validateInput(input);
  const params = parametersFor(input.saleDate);
  const annexSource = input.saleDate.startsWith("2024-")
    ? LEGAL_SOURCES.annex8_2024
    : input.saleDate.startsWith("2025-")
      ? LEGAL_SOURCES.annex8_2025
      : LEGAL_SOURCES.annex8;
  const ownership = percentage(input.ownershipPercentage, 100);
  const houseUse = input.propertyUse === "house" ? percentage(input.houseUsePercentage, 100) : decimal(0);
  const salePrice = nonNegative(input.salePrice).times(ownership);
  const udi = decimal(udiForDate(input.saleDate, input.udiValue));
  const exemptionCap = udi.times(HOUSE_EXEMPTION_UDI);
  const exemptionEligible =
    input.propertyUse === "house" &&
    input.requestHouseExemption !== false &&
    input.formalizedBeforeNotary !== false &&
    input.claimedHouseExemptionLastThreeYears !== true;
  const houseConsideration = salePrice.times(houseUse);
  const exemptConsideration = exemptionEligible
    ? minDecimal(houseConsideration, exemptionCap)
    : decimal(0);
  const taxableConsideration = Decimal.max(salePrice.minus(exemptConsideration), 0);
  const nonExemptRatio = salePrice.isZero() ? decimal(0) : taxableConsideration.div(salePrice);

  const salePreviousMonth = previousMonthKey(input.saleDate);
  const saleInpc = decimal(inpcForMonth(salePreviousMonth, input.salePreviousMonthInpc));
  const acquisitionMonth = monthKey(input.acquisitionDate);
  const acquisitionInpc = decimal(inpcForMonth(acquisitionMonth, input.acquisitionInpc));
  const acquisitionFactor = Decimal.max(saleInpc.div(acquisitionInpc), 1);
  const actualYears = completedYears(input.acquisitionDate, input.saleDate);
  const years = elapsedTaxYears(input.acquisitionDate, input.saleDate);
  const landShare = percentage(input.landPercentage, 20);
  const acquisitionCost = nonNegative(input.acquisitionCost).times(ownership);
  const landCost = acquisitionCost.times(landShare);
  const constructionCost = acquisitionCost.minus(landCost);

  const acquisitionCostDeductions: RealEstateDeductionLine[] = [
    deductionLine("land", "Costo actualizado del terreno", landCost, landCost, acquisitionFactor, nonExemptRatio),
    deductionLine(
      "construction",
      "Costo actualizado de la construcción",
      constructionCost,
      constructionReduction(constructionCost, actualYears),
      acquisitionFactor,
      nonExemptRatio,
    ),
  ];
  const updatedAcquisitionCostBeforeMinimum = acquisitionCostDeductions.reduce(
    (sum, line) => sum.plus(line.updated),
    decimal(0),
  );
  const minimumAcquisitionCost = salePrice.times(0.1);
  const minimumAcquisitionCostAdjustment = Decimal.max(
    minimumAcquisitionCost.minus(updatedAcquisitionCostBeforeMinimum),
    0,
  );
  const deductions: RealEstateDeductionLine[] = [...acquisitionCostDeductions];
  if (minimumAcquisitionCostAdjustment.greaterThan(0)) {
    deductions.push({
      key: "minimum-acquisition-cost",
      label: "Ajuste al costo mínimo legal",
      original: 0,
      adjustedBase: money(minimumAcquisitionCostAdjustment),
      updateFactor: 1,
      updated: money(minimumAcquisitionCostAdjustment),
      deductible: money(minimumAcquisitionCostAdjustment.times(nonExemptRatio)),
    });
  }

  const addAcquisitionExpense = (key: string, label: string, value = 0) => {
    const original = nonNegative(value).times(ownership);
    if (original.greaterThan(0)) {
      deductions.push(deductionLine(key, label, original, original, acquisitionFactor, nonExemptRatio));
    }
  };
  addAcquisitionExpense("acquisition-notary", "Gastos notariales de adquisición", input.acquisitionNotaryExpenses);
  addAcquisitionExpense("acquisition-taxes", "Impuestos y derechos de adquisición", input.acquisitionTaxesAndDuties);
  addAcquisitionExpense("acquisition-commissions", "Comisiones por la adquisición", input.acquisitionCommissions);

  const improvement = nonNegative(input.improvementCost ?? 0).times(ownership);
  if (improvement.greaterThan(0)) {
    if (!input.improvementDate) throw new Error("Captura la fecha de las mejoras.");
    if (input.improvementDate < input.acquisitionDate || input.improvementDate > input.saleDate) {
      throw new Error("La fecha de mejoras debe estar entre la adquisición y la venta.");
    }
    const improvementYears = completedYears(input.improvementDate, input.saleDate);
    const improvementInpc = decimal(
      inpcForMonth(monthKey(input.improvementDate), input.improvementInpc),
    );
    deductions.push(
      deductionLine(
        "improvements",
        "Construcciones, mejoras y ampliaciones",
        improvement,
        constructionReduction(improvement, improvementYears),
        Decimal.max(saleInpc.div(improvementInpc), 1),
        nonExemptRatio,
      ),
    );
  }

  const addSaleExpense = (key: string, label: string, value = 0) => {
    const original = nonNegative(value).times(ownership);
    if (original.greaterThan(0)) {
      deductions.push(deductionLine(key, label, original, original, decimal(1), nonExemptRatio));
    }
  };
  addSaleExpense("sale-notary", "Gastos notariales de la venta", input.saleNotaryExpenses);
  addSaleExpense("appraisal", "Avalúos", input.appraisalExpenses);
  addSaleExpense("commissions", "Comisiones y mediaciones", input.commissions);

  const deductibleTotal = deductions.reduce(
    (sum, line) => sum.plus(line.deductible),
    decimal(0),
  );
  const taxableGain = Decimal.max(taxableConsideration.minus(deductibleTotal), 0);
  const gainPerYear = taxableGain.div(years);
  const tariff = input.saleDate.startsWith("2026-")
    ? ANNUAL_TAX_TABLE_2026
    : ANNUAL_TAX_TABLE_2024_2025;
  const tariffCalculation = calculateTaxTableBreakdown(
    gainPerYear.toNumber(),
    tariff,
  );
  const federalTaxBeforeStateCredit = decimal(
    money(decimal(tariffCalculation.tax).times(years)),
  );
  const statePaymentAtFivePercent = money(taxableGain.times(0.05));
  const statePayment = decimal(
    money(minDecimal(statePaymentAtFivePercent, federalTaxBeforeStateCredit)),
  );
  const federalBalance = federalTaxBeforeStateCredit.minus(statePayment);

  const warnings: string[] = [
    "Es una estimación de la operación. El fedatario y la declaración anual pueden determinar importes distintos.",
  ];
  if (!exemptionEligible && input.requestHouseExemption !== false && input.propertyUse === "house") {
    warnings.push("No se aplicó la exención de casa habitación porque no se cumplen las condiciones declaradas.");
  }
  if (taxableGain.isZero() && taxableConsideration.greaterThan(0)) {
    warnings.push("La operación no genera ganancia gravable en esta estimación; el tratamiento de pérdidas no está modelado.");
  }
  if ((input.landPercentage ?? 20) === 20) {
    warnings.push("Se usó 20% del costo de adquisición para terreno; ajusta el dato si tu escritura separa terreno y construcción.");
  }

  const totalTax = federalTaxBeforeStateCredit.toNumber();
  return {
    schemaVersion: 1,
    rulesVersion: `${params.version}.inpc-${FISCAL_INDICATOR_CUTOFFS.inpc}.udi-${FISCAL_INDICATOR_CUTOFFS.udi}`,
    calculatedAt: new Date().toISOString(),
    kind: "real-estate-isr",
    title: "ISR por enajenación de inmuebles",
    salary: { daily: 0, integratedDaily: 0 },
    totals: {
      gross: money(salePrice),
      exempt: money(exemptConsideration),
      taxable: money(taxableConsideration),
      isr: totalTax,
      socialSecurity: 0,
      otherDeductions: money(deductibleTotal),
      net: totalTax,
    },
    lines: [
      { key: "seller-sale-price", label: "Contraprestación del enajenante", amount: money(salePrice), category: "transaction" },
      { key: "house-exemption", label: "Ingreso exento por casa habitación", amount: money(exemptConsideration), category: "transaction", legalBasis: "LISR, artículo 93, fracción XIX, inciso a)" },
      { key: "taxable-consideration", label: "Contraprestación no exenta", amount: money(taxableConsideration), category: "transaction" },
      ...deductions.map((line) => ({
        key: line.key,
        label: line.label,
        amount: line.deductible,
        note: `Actualizado: ${formatMxn(line.updated)}; proporción deducible ${(nonExemptRatio.times(100)).toDecimalPlaces(2).toString()}%.`,
        category: "basis" as const,
        legalBasis: "LISR, artículos 121 y 124",
      })),
      { key: "taxable-gain", label: "Ganancia gravable", amount: money(taxableGain), category: "basis" },
      { key: "federal-tax", label: "ISR de la operación antes del acreditamiento estatal", amount: totalTax, category: "federal", legalBasis: "LISR, artículo 126" },
      { key: "state-payment", label: "Pago a la entidad federativa", amount: money(statePayment), category: "state", legalBasis: "LISR, artículo 127" },
      { key: "federal-balance", label: "Saldo a enterar a la Federación", amount: money(federalBalance), category: "federal", note: "El pago estatal se acredita; no se suma al ISR total." },
    ],
    warnings,
    assumptions: [
      `Participación del enajenante: ${ownership.times(100).toDecimalPlaces(2).toString()}%.`,
      `Años transcurridos usados en la tarifa: ${years} (máximo legal de 20).`,
      `UDI de la venta: ${udi.toFixed(6)}; INPC del mes anterior a la venta: ${saleInpc.toString()}.`,
      "Los gastos de venta se consideran erogados en el mes de la operación y sin factor adicional.",
      "No se modelan pagos en parcialidades, bienes afectos a actividad empresarial ni fechas distintas para adquisición de terreno y construcción.",
    ],
    citations: [
      REAL_ESTATE_SOURCES.exemption,
      REAL_ESTATE_SOURCES.deductions,
      REAL_ESTATE_SOURCES.adjustment,
      REAL_ESTATE_SOURCES.federalPayment,
      REAL_ESTATE_SOURCES.statePayment,
      REAL_ESTATE_SOURCES.tariff,
      annexSource,
    ],
    realEstate: {
      totalSalePrice: money(input.salePrice),
      sellerSalePrice: money(salePrice),
      ownershipPercentage: ownership.times(100).toNumber(),
      houseUsePercentage: houseUse.times(100).toNumber(),
      houseConsideration: money(houseConsideration),
      exemptionEligible,
      exemptionConditions: {
        isHouse: input.propertyUse === "house",
        requested: input.requestHouseExemption !== false,
        formalizedBeforeNotary: input.formalizedBeforeNotary !== false,
        notClaimedLastThreeYears:
          input.claimedHouseExemptionLastThreeYears !== true,
      },
      udiValue: udi.toNumber(),
      exemptionCap: money(exemptionCap),
      exemptConsideration: money(exemptConsideration),
      taxableConsideration: money(taxableConsideration),
      nonExemptRatio: nonExemptRatio.toNumber(),
      salePreviousMonthInpc: saleInpc.toNumber(),
      acquisitionInpc: acquisitionInpc.toNumber(),
      actualElapsedYears: actualYears,
      elapsedYears: years,
      updatedAcquisitionCostBeforeMinimum: money(
        updatedAcquisitionCostBeforeMinimum,
      ),
      minimumAcquisitionCost: money(minimumAcquisitionCost),
      minimumAcquisitionCostAdjustment: money(
        minimumAcquisitionCostAdjustment,
      ),
      deductibleTotal: money(deductibleTotal),
      taxableGain: money(taxableGain),
      gainPerYear: money(gainPerYear),
      tariffCalculation: {
        lowerLimit: tariffCalculation.lowerLimit,
        upperLimit: tariffCalculation.upperLimit,
        fixedFee: tariffCalculation.fixedFee,
        rate: tariffCalculation.rate,
        excess: tariffCalculation.excess,
        marginalTax: tariffCalculation.marginalTax,
        taxPerYear: tariffCalculation.tax,
      },
      federalTaxBeforeStateCredit: totalTax,
      statePaymentAtFivePercent,
      statePayment: money(statePayment),
      federalBalance: money(federalBalance),
      deductions,
      indicatorCutoffs: FISCAL_INDICATOR_CUTOFFS,
    },
  };
}
